/**
 * <product-colour-gallery> - keeps the PDP gallery to ONE colour.
 *
 * Slides, thumbnails and the zoom (photoswipe) thumbnails carry data-colour-group (see
 * snippets/product-media-group.liquid). Liquid renders the selected colour visible on first
 * load; this element re-filters them when the Renk option changes, whether or not the new
 * colour/size pair resolves to a variant.
 *
 * Strict rule (mirrors snippets/product-media-hidden.liquid):
 * - no media has a colour group: legacy, everything visible.
 * - otherwise ONLY media of the selected colour is visible. Colourless media is hidden too.
 * - selected colour has no media: never show another colour. Only the colourless media that Liquid
 *   picked as the initial fallback (data-fallback-media) may stay; otherwise show the no-media state.
 */
(function () {
  'use strict';

  if (customElements.get('product-colour-gallery')) return;

  // Deliberate give-up for a transition that never settles. Not a pacing delay:
  // nothing is committed before this, and reaching it routes to the failure
  // path, never to a blank gallery or to another colour.
  const HARD_TIMEOUT = 8000;
  // CONFIRMED failure: an error event, or a load that produced no pixels.
  const FAILED_ATTR = 'data-colour-media-failed';
  // TEMPORARY: this media simply has not arrived yet. Kept apart from
  // FAILED_ATTR because a slow image must not be banned from the gallery for
  // the rest of the page's life - it is cleared when the image finally loads,
  // and on any explicit reselection of its colour.
  const TIMEOUT_ATTR = 'data-colour-media-timeout';

  const norm = (value) =>
    String(value || '')
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');

  class ProductColourGallery extends HTMLElement {
    connectedCallback() {
      this.root = this.closest('product-info') || document;
      this.optionName = this.dataset.optionName || '';
      this.onChange = this.onChange.bind(this);
      this.root.addEventListener('change', this.onChange);

      this.zoom = this.root.querySelector('zoom-images');
      const template = this.root.querySelector('[data-pswp-thumbs-template]');
      this.thumbsTemplate = template;
      this.thumbsSource = template ? template.innerHTML : '';

      // zoom.js defines <zoom-images> deferred: wait for it, then take over its image list
      if (this.zoom) {
        customElements.whenDefined('zoom-images').then(() => {
          if (!this.isConnected || !this.zoom.images) return;
          this.allZoomImages = Array.from(this.zoom.images);
          const originalCreateZoom = this.zoom.createZoom.bind(this.zoom);
          // click handlers were bound with the ORIGINAL index: map it to the filtered list
          this.zoom.createZoom = (index) => {
            const clicked = this.allZoomImages[index];
            const visibleIndex = this.zoom.images.indexOf(clicked);
            originalCreateZoom(visibleIndex < 0 ? 0 : visibleIndex);
          };
          this.applyZoom(this.dataset.selected || '');
        });
      }
      this.applyZoom(this.dataset.selected || '');
    }

    disconnectedCallback() {
      this.root && this.root.removeEventListener('change', this.onChange);
      // invalidates any transition still waiting on an image, and drops its
      // listeners/timer
      this.transitionToken = (this.transitionToken || 0) + 1;
      this.cancelPending();
    }

    isColourInput(el) {
      if (!el || !this.optionName) return false;
      const name = el.getAttribute('name') || '';
      return name === this.optionName || name.indexOf(this.optionName + '-') === 0 || name.indexOf('options[' + this.optionName) === 0;
    }

    onChange(event) {
      const el = event.target;
      if (!this.isColourInput(el) || (el.type === 'radio' && !el.checked)) return;
      this.apply(norm(el.value));
    }

    // one visibility rule for slides, thumbs, zoom images and zoom thumbs
    rule(group) {
      const slides = Array.from(this.root.querySelectorAll('[data-product-media-list] [data-slide]'));
      const tagOf = (node) => node.getAttribute('data-colour-group') || '';
      const grouped = slides.filter((s) => tagOf(s)).length;
      const inGroup = slides.filter((s) => tagOf(s) === group).length;
      const fallback = this.dataset.fallbackMedia || '';
      const initial = this.dataset.selected || '';
      const idOf = (node) => {
        const slide = node.closest('[data-slide]') || node;
        return slide.getAttribute('data-media-id') || '';
      };
      const shows = (node) => {
        const slide = node.closest('[data-slide]') || node;
        // a slide whose image proved unpaintable is not a valid result for this
        // colour: dropping it lets `empty` below reach the theme's own
        // [data-no-media] state instead of showing a broken frame
        if (slide.hasAttribute && (slide.hasAttribute(FAILED_ATTR) || slide.hasAttribute(TIMEOUT_ATTR))) return false;
        if (!grouped) return true;
        const tag = tagOf(node);
        if (inGroup > 0) return !!group && tag === group;
        return !tag && !!fallback && group === initial && idOf(node) === fallback;
      };
      return { slides, shows, empty: grouped > 0 && !slides.some((s) => shows(s)) };
    }

    applyZoom(group) {
      const { shows } = this.rule(group);
      if (this.zoom && this.allZoomImages) {
        this.zoom.images = this.allZoomImages.filter((img) => shows(img.closest('[data-slide]') || img));
      }
      if (this.thumbsTemplate && this.thumbsSource) {
        const holder = document.createElement('div');
        holder.innerHTML = this.thumbsSource;
        holder.querySelectorAll(':scope > [data-colour-group]').forEach((img) => {
          if (!shows(img)) img.remove();
        });
        // colourless zoom thumbs cannot carry a fallback: they are hidden when the product is grouped
        if (this.rule(group).slides.some((s) => s.getAttribute('data-colour-group'))) {
          holder.querySelectorAll(':scope > :not([data-colour-group])').forEach((el) => el.remove());
        }
        this.thumbsTemplate.innerHTML = holder.innerHTML;
      }
    }

    // A colour-hidden slide is display:none, which does NOT stop playback: a video the
    // customer started on the previous colour keeps running - and keeps playing audio if
    // they unmuted it - behind the new colour's gallery. Same pause idiom as the theme's
    // window.theme.pauseAllMedia, scoped to the slide so media elsewhere on the page
    // (hero, social videos) is left alone.
    pauseMedia(slide) {
      slide.querySelectorAll('video').forEach((video) => video.pause());
      slide.querySelectorAll('[data-host="youtube"]').forEach((frame) => {
        frame.contentWindow && frame.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
      });
      slide.querySelectorAll('[data-host="vimeo"]').forEach((frame) => {
        frame.contentWindow && frame.contentWindow.postMessage('{"method":"pause"}', '*');
      });
    }

    /* Race-safe colour transition.

       Before: apply() hid every non-matching slide first and only then picked
       the new colour's first slide. The incoming image is a lazy-image, so
       between those two steps the gallery had nothing painted - the blank
       beige frame in the merchant's iPhone recording.

       Now visibility is committed only once the incoming image is PROVEN to be
       paintable, and the four outcomes are kept apart:

         ready      complete && naturalWidth > 0  -> commit
         failed     error event, or settled with naturalWidth 0 -> commit with
                    the slide marked failed, so rule() drops it and the theme's
                    own [data-no-media] state shows. We never silently fall back
                    to a different colour.
         pending    nothing committed; the OUTGOING image stays on screen. It is
                    not presented as the new colour - the option control already
                    reads the new value and the gallery simply has not moved yet.
         cancelled  superseded by a later choice, or disconnected.

       decode() rejection is NOT treated as a network failure: Safari rejects it
       for reasons that have nothing to do with the bytes arriving, so the image
       is re-checked and, if it cannot paint yet, we keep waiting for load/error
       instead of guessing. The only deliberate give-up is HARD_TIMEOUT, which
       routes to the failure path rather than to a blank gallery or to an
       indefinite wait. */
    apply(group) {
      const token = (this.transitionToken = (this.transitionToken || 0) + 1);
      this.currentGroup = group;
      this.cancelPending();
      // Explicit reselection is a fresh chance for everything in this colour:
      // a slow image may have arrived since, and a confirmed failure may have
      // been a one-off network error. A genuinely broken image simply fails
      // again on this pass.
      this.resetColourState(group);
      this.advance(group, token);
    }

    // Walks the eligible media of ONE colour, preparing each candidate before
    // it is allowed to become visible. Never crosses into another colour: when
    // this colour runs out of candidates, commit() leaves nothing shown and the
    // theme's own [data-no-media] state takes over.
    advance(group, token) {
      if (token !== this.transitionToken || !this.isConnected) return;
      const { slides, shows } = this.rule(group);
      const candidate = slides.find((s) => shows(s));
      if (!candidate) {
        this.commit(group);
        return;
      }
      const img = candidate.querySelector('img');
      if (!img || ProductColourGallery.canPaint(img)) {
        this.commit(group);
        return;
      }
      this.awaitMedia({ img, slide: candidate, group, token });
    }

    resetColourState(group) {
      this.rule(group).slides.forEach((slide) => {
        if ((slide.getAttribute('data-colour-group') || '') !== group) return;
        slide.removeAttribute(TIMEOUT_ATTR);
        slide.removeAttribute(FAILED_ATTR);
      });
    }

    static canPaint(img) {
      return !!img && img.complete && img.naturalWidth > 0;
    }

    cancelPending() {
      if (this.pending) this.pending.cancel();
      this.pending = null;
      if (this.watchers) {
        this.watchers.forEach((cancel) => cancel());
        this.watchers.clear();
      }
    }

    // A timed-out image is still downloading. If it lands later and its colour
    // is still the shopper's current choice, clear the temporary mark and show
    // it; otherwise just clear the mark so a future reselection can use it.
    watchLateLoad(img, slide, group) {
      if (!this.watchers) this.watchers = new Set();
      const controller = new AbortController();
      const cancel = () => controller.abort();
      this.watchers.add(cancel);
      const done = () => {
        this.watchers.delete(cancel);
        controller.abort();
      };
      img.addEventListener(
        'load',
        () => {
          done();
          if (!ProductColourGallery.canPaint(img)) return;
          slide.removeAttribute(TIMEOUT_ATTR);
          if (!this.isConnected || this.currentGroup !== group) return;
          /* Take ownership before committing, through the SAME mechanism
             apply() uses. After this image timed out, advance() moved on and
             started preparing the next media of this colour; that preparation
             is now obsolete. Without this it later settled and committed a
             second time, dispatching theme:media:select again and snapping the
             gallery back after the shopper had already swiped - reproduced by
             outputs/pdp-colour-transition-20261007/race-harness.js, which saw
             a second select for the same media id. Bumping the token
             invalidates it, and cancelling the pending state drops its
             listeners and timer. Other late watchers are deliberately left
             alone: they belong to sibling media that may still recover. */
          this.transitionToken = (this.transitionToken || 0) + 1;
          if (this.pending) {
            this.pending.cancel();
            this.pending = null;
          }
          this.commit(group);
        },
        { signal: controller.signal }
      );
      img.addEventListener(
        'error',
        () => {
          done();
          slide.removeAttribute(TIMEOUT_ATTR);
          slide.setAttribute(FAILED_ATTR, '');
        },
        { signal: controller.signal }
      );
    }

    awaitMedia({ img, slide, group, token }) {
      if (img.getAttribute('loading') === 'lazy') img.setAttribute('loading', 'eager');

      const controller = new AbortController();
      let hardTimer = null;
      const state = {
        cancel() {
          if (hardTimer) clearTimeout(hardTimer);
          hardTimer = null;
          controller.abort();
        },
      };
      this.pending = state;

      const settle = (outcome) => {
        if (this.pending !== state) return;
        state.cancel();
        this.pending = null;
        if (token !== this.transitionToken || !this.isConnected) return;
        if (outcome === 'ready') {
          this.commit(group);
          return;
        }
        if (outcome === 'timeout') {
          slide.setAttribute(TIMEOUT_ATTR, '');
          this.watchLateLoad(img, slide, group);
        } else {
          slide.setAttribute(FAILED_ATTR, '');
        }
        // try the next media of the SAME colour, preparing it in turn
        this.advance(group, token);
      };

      img.addEventListener(
        'load',
        () => settle(ProductColourGallery.canPaint(img) ? 'ready' : 'failed'),
        { signal: controller.signal }
      );
      img.addEventListener('error', () => settle('failed'), { signal: controller.signal });

      if (typeof img.decode === 'function') {
        img
          .decode()
          .then(() => settle('ready'))
          .catch(() => {
            // not proof of a network failure - only act if it can already paint
            if (ProductColourGallery.canPaint(img)) settle('ready');
          });
      }

      hardTimer = setTimeout(
        () => settle(ProductColourGallery.canPaint(img) ? 'ready' : 'timeout'),
        HARD_TIMEOUT
      );
    }

    commit(group) {
      const { slides, shows, empty } = this.rule(group);
      const thumbs = Array.from(this.root.querySelectorAll('[data-thumb-item]'));

      slides.forEach((s) => {
        const hide = !shows(s);
        if (hide && !s.hasAttribute('data-colour-hidden')) this.pauseMedia(s);
        s.toggleAttribute('data-colour-hidden', hide);
      });
      thumbs.forEach((t) => t.toggleAttribute('data-colour-hidden', !shows(t)));
      const placeholder = this.root.querySelector('[data-no-media]');
      if (placeholder) placeholder.hidden = !empty;

      this.applyZoom(group);

      // reset to the new colour's primary image
      const first = slides.find((s) => !s.hasAttribute('data-colour-hidden'));
      if (first) {
        first.dispatchEvent(
          new CustomEvent('theme:media:select', { bubbles: true, detail: { id: first.getAttribute('data-media-id') } })
        );
        const top = first.getBoundingClientRect().top;
        if (top < 0 && window.matchMedia('(min-width: 750px)').matches) {
          window.scrollTo({ top: Math.max(0, window.scrollY + top - 140) });
        }
      }
    }
  }

  customElements.define('product-colour-gallery', ProductColourGallery);
})();
